import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const client = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// crypto.randomUUID() is restricted to secure contexts on some browsers.
// Fall back to a cheap Math.random-based ID on plain-HTTP dev deployments
// so the request interceptor never throws before axios dispatches.
function makeRequestId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return (
    'req-' +
    Date.now().toString(36) +
    '-' +
    Math.random().toString(36).slice(2, 10)
  );
}

// Request interceptor: attach request ID and optional Bearer token
client.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      config.headers.set('X-Request-ID', makeRequestId());
    } catch {
      /* a failure here must never abort the request */
    }

    // In dev mode without MSAL, skip token acquisition
    // In production, dynamically import and use MSAL
    try {
      const { msalInstance } = await import('@/auth/AuthProvider');
      if (!msalInstance) {
        const { getStoredToken } = await import('@/auth/localToken');
        const localToken = getStoredToken();
        if (localToken) {
          config.headers.set('Authorization', `Bearer ${localToken}`);
        }
        return config;
      }
      const { loginRequest } = await import('@/auth/config');
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        const response = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        config.headers.set('Authorization', `Bearer ${response.accessToken}`);
      }
    } catch {
      // MSAL not configured or token acquisition failed — proceed without auth
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor: handle errors
client.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response) {
      const { status } = error.response;

      if (status === 401) {
        const requestUrl = error.config?.url ?? '';
        const onLoginFlow =
          window.location.pathname.startsWith('/login') ||
          window.location.pathname.startsWith('/change-password') ||
          requestUrl.includes('/auth/local/change-password');

        if (!onLoginFlow) {
          try {
            const { getAuthConfig } = await import('@/api/auth');
            const cfg = await getAuthConfig();
            if (cfg.auth_enforced) {
              const { clearStoredToken } = await import('@/auth/localToken');
              clearStoredToken();
              window.location.href = '/login';
            }
          } catch {
            console.warn('Unauthorized');
          }
        }
      }

      if (status === 403) {
        console.error('Forbidden: You do not have permission to access this resource.');
      }

      if (status >= 500) {
        console.error('Server error:', error.response.data);
      }
    }

    return Promise.reject(error);
  },
);

export default client;
